import { axiosInstance, dataServiceApi } from "contexts/axiosApiInstance";
import { wasmBody } from "components/common/wasmBody";
import {
  TableDetails,
  TableSourceJS,
  CreateCheckpointRequest,
  AddCheckpointAlias,
  RemoveCheckpointAlias,
  SetCheckpointAliasAsDefault,
  EnsureCheckpointRequest,
  SetTableReleaseRequest,
  ClearTableReleaseRequest,
  Checkpoint,
} from "target_wasm";
import { isTerminalImportedState } from "components/canvas/utils/roles";
import { createLogger } from "debug/DebugingLogger";
import { releaseRefusal, unreleaseRefusal } from "./utils";
const log = createLogger("HistoryServices");

export const loadCheckpoints = async (
  tableId: string,
  tableDetails: TableDetails,
): Promise<Checkpoint[]> => {
  const response = await dataServiceApi.get(`/tables/${tableId}/checkpoints`);
  const checkpoints: Checkpoint[] = response.data.map((checkpoint: unknown) =>
    Checkpoint.from_js(checkpoint),
  );
  return tableDetails.source === TableSourceJS.ImportedInactive ||
    isTerminalImportedState(tableDetails.source)
    ? checkpoints.filter(
        (checkpoint) =>
          checkpoint.table_version_number() <= Number(tableDetails.version),
      )
    : checkpoints;
};

export const createCheckpoint = async (
  tableId: string,
  alias: string,
  tableVersion: number,
  createdBy: string,
): Promise<Checkpoint> => {
  const request = CreateCheckpointRequest.new_js(
    tableId,
    alias,
    BigInt(tableVersion),
    createdBy,
  );
  const response = await dataServiceApi.post(
    "/tables/checkpoints/create",
    wasmBody(request),
  );
  return Checkpoint.from_js(response.data);
};

/** The checkpoint at `version`, made as `temp-checkpoint-{N}` if missing, so a diff can anchor there. */
export const ensureCheckpoint = async (
  tableId: string,
  version: number,
): Promise<string> => {
  const request = EnsureCheckpointRequest.new_js(tableId, BigInt(version));
  const response = await dataServiceApi.post(
    "/tables/checkpoints/ensure",
    wasmBody(request),
  );
  return response.data.checkpoint_id;
};

/** Names the checkpoint `alias`; its old name stays one of its aliases. */
export const addCheckpointAlias = async (
  tableId: string,
  checkpointId: string,
  alias: string,
): Promise<void> => {
  const request = AddCheckpointAlias.new_js(tableId, checkpointId, alias);
  await dataServiceApi.post(
    "/tables/checkpoints/add_checkpoint_alias",
    wasmBody(request),
  );
};

export const setAliasAsDefault = async (
  tableId: string,
  checkpointId: string,
  alias: string,
): Promise<void> => {
  const request = SetCheckpointAliasAsDefault.new_js(
    tableId,
    checkpointId,
    alias,
  );
  await dataServiceApi.post(
    "/tables/checkpoints/set_alias_as_default_name",
    wasmBody(request),
  );
};

export const removeCheckpointAlias = async (
  tableId: string,
  checkpointId: string,
  alias: string,
): Promise<void> => {
  const request = RemoveCheckpointAlias.new_js(tableId, checkpointId, alias);
  await dataServiceApi.delete("/tables/checkpoints/remove_checkpoint_alias", {
    data: wasmBody(request),
  });
};

export interface TableRelease {
  checkpointId: string;
  version: number;
}

export interface ReleaseHistoryEntry {
  checkpointId: string;
  version: number;
  releasedAt: number;
  releasedBy: string;
  name: string;
}

export const listReleases = async (
  tableId: string,
): Promise<ReleaseHistoryEntry[]> => {
  try {
    const response = await dataServiceApi.get(`/tables/${tableId}/releases`);
    return (response.data ?? []).map(
      (r: {
        checkpoint_id: string;
        version: number;
        released_at: number;
        released_by: string;
        name: string;
      }) => ({
        checkpointId: r.checkpoint_id,
        version: Number(r.version),
        releasedAt: Number(r.released_at),
        releasedBy: r.released_by,
        name: r.name,
      }),
    );
  } catch (err) {
    log.error("Failed to list releases: %o", err);
    throw new Error("Failed to load release history", { cause: err });
  }
};

/** Tells importing models the release changed; best-effort, as they also reconcile. */
const notifyRelease = async (tableId: string): Promise<void> => {
  try {
    await axiosInstance.post(
      `/api/tables/${encodeURIComponent(tableId)}/notify-release`,
    );
  } catch (err) {
    log.warning(
      "notify-release broadcast failed (best-effort) for %s: %o",
      tableId,
      err,
    );
  }
};

/** Releases `version`; the server checkpoints a version that has none. */
export const setRelease = async (
  tableId: string,
  version: number,
): Promise<TableRelease> => {
  try {
    const releasedBy = String(localStorage.getItem("email") || "unknown");
    const request = SetTableReleaseRequest.new_js(
      tableId,
      BigInt(version),
      releasedBy,
    );
    const response = await dataServiceApi.post(
      "/tables/checkpoints/set_release",
      wasmBody(request),
    );
    await notifyRelease(tableId);
    return {
      checkpointId: response.data.checkpoint_id,
      version: Number(response.data.version),
    };
  } catch (err) {
    log.error("Failed to set release: %o", err);
    throw new Error(releaseRefusal(err), { cause: err });
  }
};

/** Releases `version` and names its checkpoint, as a dataset release does. */
export const publishNamedRelease = async (
  tableId: string,
  version: number,
  name: string,
): Promise<TableRelease> => {
  const release = await setRelease(tableId, version);
  const alias = name.trim();
  if (alias) await addCheckpointAlias(tableId, release.checkpointId, alias);
  return release;
};

/** Clear a table's released version, reverting serving to live latest. */
export const clearRelease = async (tableId: string): Promise<void> => {
  try {
    const request = ClearTableReleaseRequest.new_js(tableId);
    await dataServiceApi.post(
      "/tables/checkpoints/clear_release",
      wasmBody(request),
    );
    await notifyRelease(tableId);
  } catch (err) {
    log.error("Failed to clear release: %o", err);
    throw new Error(unreleaseRefusal(err), { cause: err });
  }
};

export const getRelease = async (
  tableId: string,
): Promise<TableRelease | null> => {
  try {
    const response = await dataServiceApi.get(`/tables/${tableId}/release`);
    if (!response.data) {
      return null;
    }
    return {
      checkpointId: response.data.checkpoint_id,
      version: Number(response.data.version),
    };
  } catch (err) {
    log.error("Failed to get release: %o", err);
    throw new Error("Failed to get release", { cause: err });
  }
};
